import React, { useState, useEffect, useRef } from 'react';
import { paymentsAPI, billsAPI } from '../services/api';
import ApiSelect from '../components/ApiSelect';
import PaymentFilterSection from '../components/PaymentFilterSection';
import { useToast } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { SkeletonTable, SkeletonCards } from '../components/Skeleton';
import FYPicker from '../components/FYPicker';
import { getCurrentFY, getFYLabel, getAdjacentFYs } from '../utils/financialYear';
import { billOptionValue, billToOption } from '../utils/billOption';

const Payments = () => {
  const toast = useToast();
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, id: null });
  const [selectedFY, setSelectedFY] = useState(getCurrentFY());
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [formData, setFormData] = useState({
    company: null,
    party: null,
    bills: [], // Array of selected bills
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    financialYear: getCurrentFY()
  });

  // Track if multiple bills are selected (for read-only amount field)
  const [isMultipleBills, setIsMultipleBills] = useState(false);
  const [validationError, setValidationError] = useState('');
  // The payment that already exists for a selected (single) bill — drives the
  // "edit instead of creating new" warning and shortcut. null when none.
  const [existingPayment, setExistingPayment] = useState(null);

  // Single filter state (like Reports page)
  const [filters, setFilters] = useState({
    company: null,
    party: null,
    billNumber: ''
  });

  // Separate state for bill number input (for debouncing)
  const [billNumberInput, setBillNumberInput] = useState('');
  const billNumberTimeoutRef = useRef(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 15,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Combined effect with debounce for bill number search, immediate for other filters and pagination
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPayments();
    }, filters.billNumber ? 1000 : 0); // Debounce only for bill number search, immediate for others

    return () => clearTimeout(timeoutId);
  }, [filters, pagination.currentPage, selectedFY]);

  // Reset pagination when filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [filters]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage
      };

      if (filters.company) params.company = filters.company._id;
      if (filters.party) params.party = filters.party._id;
      if (filters.billNumber) params.billNumber = filters.billNumber;
      if (selectedFY) params.fy = selectedFY;

      const response = await paymentsAPI.getAll(params);
      setPayments(response.data.payments);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      // Auto-compute FY when paymentDate changes
      if (name === 'paymentDate' && value) {
        updated.financialYear = getFYLabel(value);
      }
      return updated;
    });
  };

  const handleCompanySelect = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      company: selectedOption ? selectedOption.data : null
    }));
  };

  const handlePartySelect = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      party: selectedOption ? selectedOption.data : null
    }));
  };

  const handleBillNumberSelect = async (selectedOptions) => {
    setValidationError('');
    setExistingPayment(null);

    // Handle clear/empty selection
    if (!selectedOptions || (Array.isArray(selectedOptions) && selectedOptions.length === 0)) {
      setFormData(prev => ({
        ...prev,
        bills: [],
        amount: '',
        company: null,
        party: null
      }));
      setIsMultipleBills(false);
      return;
    }

    // Convert to array for consistent handling
    const selectedBills = Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions];
    const bills = selectedBills.map(option => option.data);

    // Check if multiple bills selected
    const isMultiple = bills.length > 1;
    setIsMultipleBills(isMultiple);

    if (isMultiple) {
      // Validate all bills belong to same company and party
      const firstBill = bills[0];
      const allSameCompany = bills.every(bill =>
        bill.company?._id === firstBill.company?._id ||
        bill.company === firstBill.company
      );
      const allSameParty = bills.every(bill =>
        bill.party?._id === firstBill.party?._id ||
        bill.party === firstBill.party
      );

      if (!allSameCompany || !allSameParty) {
        setValidationError('All selected bills must belong to the same company and party');
        return;
      }

      // Fetch pending amounts for all bills
      try {
        const response = await billsAPI.getBulkPendingAmounts(bills.map(b => b._id));
        const results = response.data.results;

        // One invoice = one payment: block the batch if any selected bill is already paid.
        const alreadyPaid = results
          .filter(result => result.existingPayment)
          .map(result => result.bill.billNumber);
        if (alreadyPaid.length > 0) {
          setValidationError(
            `Payment already exists for: ${alreadyPaid.join(', ')}. Remove them, or edit those payments instead.`
          );
        }

        // Calculate total pending amount
        const totalPending = results.reduce((sum, result) => sum + result.pendingAmount, 0);

        // Update form data
        setFormData(prev => ({
          ...prev,
          bills: bills,
          company: firstBill.company || null,
          party: firstBill.party || null,
          amount: totalPending > 0 ? totalPending.toString() : ''
        }));
      } catch (error) {
        console.error('Error fetching bulk pending amounts:', error);
        setValidationError('Error fetching pending amounts');
      }
    } else {
      // Single bill - use existing flow
      const bill = bills[0];

      // Fetch and auto-fill pending amount
      let pendingAmount = '';
      try {
        const response = await billsAPI.getPendingAmount(bill._id);
        pendingAmount = response.data.pendingAmount > 0 ? response.data.pendingAmount.toString() : '';

        // One invoice = one payment: warn (and block) if this bill is already paid.
        // Ignore the payment we're currently editing so editing its own bill doesn't trip.
        const existing = response.data.existingPayment;
        if (existing && existing._id !== editingPayment?._id) {
          setExistingPayment(existing);
          setValidationError(
            `A payment already exists for invoice ${bill.billNumber} (FY ${bill.financialYear}). Edit it instead of creating a new one.`
          );
          pendingAmount = ''; // don't prefill an amount for a payment that can't be created
        }
      } catch (error) {
        console.error('Error fetching pending amount:', error);
      }

      // Update all fields in a single state update
      setFormData(prev => ({
        ...prev,
        bills: [bill],
        company: bill.company || null,
        party: bill.party || null,
        amount: pendingAmount
      }));
    }
  };

  const handleCompanyFilterSelect = (selectedOption) => {
    const company = selectedOption ? selectedOption.data : null;
    setFilters(prev => ({
      ...prev,
      company: company
    }));
  };

  const handlePartyFilterSelect = (selectedOption) => {
    const party = selectedOption ? selectedOption.data : null;
    setFilters(prev => ({
      ...prev,
      party: party
    }));
  };

  const handleBillNumberFilterChange = (e) => {
    const value = e.target.value;
    setBillNumberInput(value);

    // Clear existing timeout
    if (billNumberTimeoutRef.current) {
      clearTimeout(billNumberTimeoutRef.current);
    }

    // Set new timeout for debounced filter update
    billNumberTimeoutRef.current = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        billNumber: value
      }));
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validationError) {
      toast.error(validationError);
      return;
    }

    if (!formData.company || !formData.party || !formData.amount || !formData.paymentDate) {
      toast.error('All fields are required');
      return;
    }

    try {
      // Always identify the invoice by its _id — a bill number is reused in a new financial year,
      // so the backend can't resolve the right invoice from the number alone.
      if (editingPayment) {
        // Editing mode - use single payment update (existing flow)
        const paymentData = {
          company: formData.company._id,
          party: formData.party._id,
          bill: formData.bills.length > 0 ? formData.bills[0]._id : null,
          amount: parseFloat(formData.amount),
          paymentDate: formData.paymentDate,
          financialYear: formData.financialYear
        };
        await paymentsAPI.update(editingPayment._id, paymentData);
        toast.success('Payment updated');
      } else {
        // Create mode
        if (isMultipleBills && formData.bills.length > 1) {
          // Multiple bills - use bulk API
          const bulkData = {
            bills: formData.bills.map(bill => bill._id),
            company: formData.company._id,
            party: formData.party._id,
            paymentDate: formData.paymentDate,
            financialYear: formData.financialYear
          };
          await paymentsAPI.createBulk(bulkData);
        } else {
          // Single bill - use existing API
          const paymentData = {
            company: formData.company._id,
            party: formData.party._id,
            bill: formData.bills.length > 0 ? formData.bills[0]._id : null,
            amount: parseFloat(formData.amount),
            paymentDate: formData.paymentDate,
            financialYear: formData.financialYear
          };
          await paymentsAPI.create(paymentData);
        }
        toast.success('Payment recorded');
      }

      resetForm();
      fetchPayments(); // Refresh after create/update
    } catch (error) {
      console.error('Error saving payment:', error);
      toast.error('Error saving payment: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      company: payment.company,
      party: payment.party,
      // The payment carries the invoice it settles. Carry the populated company across so the
      // option renders its "<number> (<company>)" label the same way the dropdown does.
      bills: payment.bill ? [{ ...payment.bill, company: payment.company }] : [],
      amount: payment.amount.toString(),
      paymentDate: new Date(payment.paymentDate).toISOString().split('T')[0],
      financialYear: payment.financialYear || getFYLabel(payment.paymentDate)
    });
    setIsMultipleBills(false);
    setValidationError('');
    setExistingPayment(null);
    setShowForm(true);
  };

  // Jump straight to editing the payment that already exists for the selected bill.
  // Fetch the full (populated) payment so company/party render correctly in the form.
  const handleEditExisting = async () => {
    if (!existingPayment) return;
    try {
      const { data } = await paymentsAPI.getById(existingPayment._id);
      handleEdit(data);
    } catch (error) {
      console.error('Error loading existing payment:', error);
      toast.error('Could not load the existing payment');
    }
  };

  const handleDelete = (id) => {
    setConfirmDialog({ isOpen: true, id });
  };

  const confirmDelete = async () => {
    try {
      await paymentsAPI.delete(confirmDialog.id);
      toast.success('Payment deleted');
      fetchPayments();
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error('Error deleting payment');
    } finally {
      setConfirmDialog({ isOpen: false, id: null });
    }
  };

  const resetForm = () => {
    setFormData({
      company: null,
      party: null,
      bills: [],
      amount: '',
      paymentDate: new Date().toISOString().split('T')[0],
      financialYear: getCurrentFY()
    });
    setIsMultipleBills(false);
    setValidationError('');
    setExistingPayment(null);
    setEditingPayment(null);
    setShowForm(false);
  };

  const clearFilters = () => {
    setFilters({
      company: null,
      party: null,
      billNumber: ''
    });
    setBillNumberInput('');

    // Clear any pending timeout
    if (billNumberTimeoutRef.current) {
      clearTimeout(billNumberTimeoutRef.current);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-brand-900">Payments</h1>
            <p className="text-gray-500 mt-1 sm:mt-2 text-sm sm:text-base">Manage your payments</p>
          </div>
          <FYPicker value={selectedFY} onChange={(fy) => { setSelectedFY(fy); setPagination(prev => ({...prev, currentPage: 1})); }} />
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="w-full sm:w-auto bg-brand-500 text-white px-4 py-2.5 rounded-xl hover:bg-brand-600 shadow-sm shadow-brand-500/25 transition-colors text-base font-medium"
        >
          Add Payment
        </button>
      </div>

      {/* Filters */}
      <PaymentFilterSection
        filterValues={{...filters, billNumber: billNumberInput}}
        onCompanySelect={handleCompanyFilterSelect}
        onPartySelect={handlePartyFilterSelect}
        onBillNumberChange={handleBillNumberFilterChange}
        onClearFilters={clearFilters}
      />

      {/* Loading State */}
      {loading && (
        <>
          <div className="hidden sm:block">
            <SkeletonTable rows={5} cols={5} />
          </div>
          <div className="block sm:hidden">
            <SkeletonCards count={3} />
          </div>
        </>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-2xl border border-brand-100 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-brand-900 mb-4">
            {editingPayment ? 'Edit Payment' : 'Add New Payment'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Bill Number(s) (Optional)
                </label>
                <ApiSelect
                  type="bills"
                  value={formData.bills.map(billOptionValue)}
                  onChange={handleBillNumberSelect}
                  placeholder="Select bill number(s)"
                  fetchAll={true}
                  fy={selectedFY}
                  extraOptions={formData.bills.map(billToOption)}
                  isDisabled={false}
                  isMulti={true}
                />
                {validationError && (
                  <div className="mt-1 flex flex-col sm:flex-row sm:items-center gap-2">
                    <p className="text-sm text-red-600">{validationError}</p>
                    {existingPayment && (
                      <button
                        type="button"
                        onClick={handleEditExisting}
                        className="self-start sm:self-auto px-2.5 py-1 rounded-lg text-xs font-medium bg-brand-50 text-brand-600 hover:bg-brand-100 transition-colors whitespace-nowrap"
                      >
                        Edit existing payment
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Party <span className="text-red-500">*</span>
                </label>
                <ApiSelect
                  type="parties"
                  value={formData.party?._id || null}
                  onChange={handlePartySelect}
                  placeholder="Select party"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Company <span className="text-red-500">*</span>
                </label>
                <ApiSelect
                  type="companies"
                  value={formData.company?._id || null}
                  onChange={handleCompanySelect}
                  placeholder="Select company"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Amount <span className="text-red-500">*</span>
                  {isMultipleBills && (
                    <span className="text-xs text-gray-400 ml-2">(Auto-calculated from pending amounts)</span>
                  )}
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  readOnly={isMultipleBills}
                  className={`w-full px-3 py-2 border border-brand-100 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-base tabular-nums ${
                    isMultipleBills ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  placeholder="Enter amount"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Payment Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="paymentDate"
                  value={formData.paymentDate}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-brand-100 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Financial Year
                </label>
                <select
                  value={formData.financialYear}
                  onChange={(e) => setFormData(prev => ({ ...prev, financialYear: e.target.value }))}
                  className="w-full px-3 py-2 border border-brand-100 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-base"
                >
                  {getAdjacentFYs(formData.financialYear).map(fy => (
                    <option key={fy} value={fy}>FY {fy}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="submit"
                className="w-full sm:w-auto bg-emerald-600 text-white px-5 py-2.5 rounded-xl hover:bg-emerald-700 transition-colors text-base font-medium shadow-sm"
              >
                {editingPayment ? 'Update Payment' : 'Add Payment'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="w-full sm:w-auto bg-white text-gray-500 border border-brand-100 px-5 py-2.5 rounded-xl hover:bg-brand-50/50 transition-colors text-base font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Payments List */}
      <div className="bg-white rounded-2xl border border-brand-100 overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-brand-100">
          <h2 className="text-lg font-semibold text-brand-900">All Payments</h2>
        </div>

        {payments.length === 0 ? (
          <div className="p-4 sm:p-6 text-center text-gray-500">
            {pagination.totalItems === 0 ? 'No payments found. Add your first payment to get started.' : 'No payments match the current filters.'}
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block sm:hidden">
              {payments.map((payment) => (
                <div key={payment._id} className="p-4 border-b border-brand-100 last:border-b-0 hover:bg-brand-50/50 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-brand-900">
                      {payment.billNumber || 'N/A'}
                    </div>
                    <div className="text-sm font-semibold text-brand-900 tabular-nums">
                      {'\u20B9'}{payment.amount.toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mb-3 space-y-0.5">
                    <div>Date: {new Date(payment.paymentDate).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}</div>
                    <div>Party: {payment.party?.name || 'N/A'}</div>
                    <div>Company: {payment.company?.name || 'N/A'}</div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(payment)}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium bg-brand-50 text-brand-600 hover:bg-brand-100 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(payment._id)}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-brand-100">
                <thead className="bg-brand-50/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Party
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Bill Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-brand-100">
                  {payments.map((payment) => (
                    <tr key={payment._id} className="hover:bg-brand-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {new Date(payment.paymentDate).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-brand-900">
                          {payment.party?.name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-brand-900">
                          {payment.company?.name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-brand-900">
                          {payment.billNumber || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-brand-900 tabular-nums">
                          {'\u20B9'}{payment.amount.toLocaleString('en-IN')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(payment)}
                            className="px-2.5 py-1 rounded-lg text-xs font-medium bg-brand-50 text-brand-600 hover:bg-brand-100 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(payment._id)}
                            className="px-2.5 py-1 rounded-lg text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {pagination.totalPages > 1 && (
              <div className="px-4 py-3 flex items-center justify-between border-t border-brand-100 sm:px-6">
                {/* Mobile Pagination */}
                <div className="flex sm:hidden justify-between items-center w-full">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrevPage}
                    className="relative inline-flex items-center px-3 py-2 border border-brand-100 text-sm font-medium rounded-xl text-gray-500 bg-white hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-500">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                    className="relative inline-flex items-center px-3 py-2 border border-brand-100 text-sm font-medium rounded-xl text-gray-500 bg-white hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>

                {/* Desktop Pagination */}
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-500">
                      Showing{' '}
                      <span className="font-medium text-brand-900">
                        {(pagination.currentPage - 1) * pagination.itemsPerPage + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium text-brand-900">
                        {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium text-brand-900">{pagination.totalItems}</span>{' '}
                      results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-xl shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={!pagination.hasPrevPage}
                        className="relative inline-flex items-center px-3 py-2 rounded-l-xl border border-brand-100 bg-white text-sm font-medium text-gray-500 hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>

                      {/* Page Numbers - Show max 5 pages */}
                      {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                        let page;
                        if (pagination.totalPages <= 5) {
                          page = i + 1;
                        } else if (pagination.currentPage <= 3) {
                          page = i + 1;
                        } else if (pagination.currentPage >= pagination.totalPages - 2) {
                          page = pagination.totalPages - 4 + i;
                        } else {
                          page = pagination.currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors ${
                              page === pagination.currentPage
                                ? 'z-10 bg-brand-500 border-brand-500 text-white'
                                : 'bg-white border-brand-100 text-gray-500 hover:bg-brand-50/50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={!pagination.hasNextPage}
                        className="relative inline-flex items-center px-3 py-2 rounded-r-xl border border-brand-100 bg-white text-sm font-medium text-gray-500 hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Payment"
        message="Are you sure you want to delete this payment? This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={() => setConfirmDialog({ isOpen: false, id: null })}
      />
    </div>
  );
};

export default Payments;
